'use client';

import NextImage, { type ImageProps as NextImageProps } from 'next/image';
import { useContext, useEffect, useState } from 'react';

import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import { ColumnContext } from '~/ui/components/grid';
import { getImageAspectRatio } from '~/ui/modules/image/getImageAspectRatio';
import { type AspectRatio, getAspectRatio, getOrientation } from '~/ui/utils/aspectRatio';
import { toBase64 } from '~/utils/base64';
import { isRelativeUrl } from '~/utils/isRelativeUrl';
import type { ImageColSpan } from '~/v1/components/image/image.interface';
import { getSizesFromSpan } from '~/v1/components/image/image.utils';

import styles from './image.module.scss';
import { imageLoader } from './loader';
import { type Image_AssetFragment } from './query';

export interface ImageProps extends Omit<NextImageProps, 'src' | 'alt'> {
  image: Masked<typeof Image_AssetFragment>;

  onCalculateAspectRatio?: (aspectRatio: AspectRatio) => void;
  aspectRatio?:
    | React.CSSProperties['aspectRatio']
    | { portrait: string; landscape: string }
    | { portrait?: string; landscape: string }
    | { portrait: string; landscape?: string };
  colSpan?: ImageColSpan;
}

export function Image({
  image,
  className,
  onLoad,
  aspectRatio: aspectRatioOverride,
  onCalculateAspectRatio,
  colSpan,
  children,
  ...rest
}: ImageProps) {
  const columns = useContext(ColumnContext);
  const { src, title, width, height } = unmask<typeof Image_AssetFragment>(image);
  const [aspectRatio, setAspectRatio] = useState(() => getImageAspectRatio(image));

  const imageSrc = src ? (src.startsWith('//') && isRelativeUrl(src) ? `https:${src}` : src) : '';

  useEffect(() => {
    if (aspectRatio) {
      onCalculateAspectRatio?.(aspectRatio);
    }
  }, [onCalculateAspectRatio, aspectRatio]);

  return (
    <div
      className={cx(styles.imageContainer, className)}
      style={{
        aspectRatio: aspectRatio
          ? determineAspectRatioToApply(aspectRatio, aspectRatioOverride)
          : undefined,
      }}
    >
      <NextImage
        src={imageSrc}
        alt={title ?? ''}
        fill
        placeholder={
          src?.endsWith('.svg') ? undefined : getImagePlaceholder(width ?? 200, height ?? 200)
        }
        // locally imported icons and assets should be optimized by next's built-in loader
        loader={src?.startsWith('/_next') ? undefined : imageLoader}
        onLoad={event => {
          const { naturalHeight, naturalWidth } = event.currentTarget;
          // aspectRatio is a primitive and won't cause a re-render if the values are the same
          setAspectRatio(getAspectRatio({ width: naturalWidth, height: naturalHeight }));
          onLoad?.(event);
        }}
        sizes={getSizesFromSpan(colSpan || columns)}
        {...rest}
      />
      {children}
    </div>
  );
}

/**
 * Determines which aspect ratio to apply to the image. `aspectRatioOverride`
 * has precedence over `naturalAspectRatio`, if defined.
 */
function determineAspectRatioToApply(
  naturalAspectRatio: AspectRatio,
  aspectRatioOverride?: ImageProps['aspectRatio'],
): React.CSSProperties['aspectRatio'] {
  if (!aspectRatioOverride) {
    return naturalAspectRatio;
  }

  if (getOrientation(naturalAspectRatio) === 'portrait') {
    return typeof aspectRatioOverride === 'object'
      ? aspectRatioOverride.portrait
      : aspectRatioOverride;
  } else {
    return typeof aspectRatioOverride === 'object'
      ? aspectRatioOverride.landscape
      : aspectRatioOverride;
  }
}

function getImagePlaceholder(w: number, h: number): `data:image/${string}` {
  const shimmer = `<svg width="${w}" height="${h}" style="opacity: 0.08;" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <rect id="r" width="${w}" height="${h}" fill="#000" />
  <animate xlink:href="#r" attributeName="opacity" values="0.2;0.8;0.2" dur="1700ms" repeatCount="indefinite" />
</svg>`;
  return `data:image/svg+xml;base64,${toBase64(shimmer)}`;
}
